import { memo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Bed, Bath, Ruler, Star, Zap, Crown } from 'lucide-react';
import { Property, getPropertyUrl } from '@/types/property';
import { formatPrice, getPropertyImage } from '@/lib/formatters';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { useAuth } from '@/contexts/AuthContext';
import { useIsFavorite, useToggleFavorite } from '@/hooks/useFavorites';
import { useTrackAdClick, useTrackAdImpression } from '@/hooks/usePropertyAds';
import { cn } from '@/lib/utils';
import { PropertyImageCarousel } from './PropertyImageCarousel';

interface PropertyCardProps {
  property: Property;
  variant?: 'default' | 'compact';
  priority?: boolean;
  /** Ad type passed from parent grid to avoid N+1 queries */
  adType?: 'featured' | 'sponsored' | 'premium';
  /** Distance in km from user's location (nearby mode) */
  distanceKm?: number;
}

export const PropertyCard = memo(function PropertyCard({ property, variant = 'default', priority = false, adType, distanceKm }: PropertyCardProps) {
  const { user } = useAuth();
  const { data: isFavorite } = useIsFavorite(property.id);
  const toggleFavorite = useToggleFavorite();
  const trackClick = useTrackAdClick();
  const trackImpression = useTrackAdImpression();
  const impressionTracked = useRef(false);

  // Track impression once when card is rendered with an active ad
  useEffect(() => {
    if (adType && !impressionTracked.current) {
      impressionTracked.current = true;
      trackImpression.mutate(property.id);
    }
  }, [adType, property.id]);

  const handleClick = () => {
    if (adType) {
      trackClick.mutate(property.id);
    }
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    toggleFavorite.mutate({ propertyId: property.id, isFavorite: !!isFavorite });
  };

  const AdBadge = () => {
    if (!adType) return null;
    
    const config = {
      featured: { label: 'Featured', icon: Star, className: 'bg-yellow-500 text-white' },
      sponsored: { label: 'Sponsored', icon: Zap, className: 'bg-blue-500 text-white' },
      premium: { label: 'Premium', icon: Crown, className: 'bg-purple-500 text-white' },
    };
    
    const { label, icon: Icon, className } = config[adType];
    
    return (
      <Badge className={cn("absolute top-2 left-2 z-10", className)}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  };

  if (variant === 'compact') {
    return (
      <Link to={getPropertyUrl(property)} className="group block" onClick={handleClick}>
        <article className="flex flex-row bg-card rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow relative">
          {adType && <AdBadge />}
          <div className="w-32 h-24 flex-shrink-0 overflow-hidden">
            <OptimizedImage
              src={getPropertyImage(property.images)}
              alt={`${property.title} - ${property.address}`}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              width={128}
              height={96}
              sizes="128px"
              className="w-full h-full"
            />
          </div>
          <div className="flex-1 p-3">
            <p className="font-bold text-lg">{formatPrice(property.price)}</p>
            <p className="text-sm text-muted-foreground line-clamp-1">{property.address}</p>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
              <span>{property.bedrooms} bd</span>
              <span>{property.bathrooms} ba</span>
              {property.sqft && <span>{property.sqft.toLocaleString()} sf</span>}
            </div>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link to={getPropertyUrl(property)} className="group block" onClick={handleClick}>
      <article className="bg-card rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300" style={{ contain: 'layout style paint' }}>
        {/* Image Carousel - fixed aspect ratio prevents CLS */}
        <div className="relative aspect-[16/10] overflow-hidden bg-muted">
          <AdBadge />
          <PropertyImageCarousel
            images={property.images}
            alt={`${property.title} - ${property.address}`}
            priority={priority}
          />
          
          {/* Favorite Button */}
          {user && (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "absolute top-2 right-2 w-8 h-8 bg-white/80 backdrop-blur-sm hover:bg-white rounded-full z-20",
                isFavorite && "text-red-500"
              )}
              onClick={handleFavoriteClick}
              disabled={toggleFavorite.isPending}
              aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Heart className={cn("w-4 h-4", isFavorite && "fill-current")} />
            </Button>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-bold text-xl">
              {formatPrice(property.price)}
            </p>
            {distanceKm !== undefined && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                {distanceKm < 1 ? `${Math.round(distanceKm * 1000)}m` : `${distanceKm.toFixed(1)} km`}
              </span>
            )}
          </div>
          
          <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
            {property.address}
          </p>

          <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
            <span className="text-foreground/70">{property.neighborhood || property.city}</span>
            <span className="mx-1">•</span>
            <span>{property.city}</span>
          </div>

          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Bed className="w-4 h-4 text-muted-foreground" />
              <span>{property.bedrooms} bd</span>
            </div>
            <div className="flex items-center gap-1">
              <Bath className="w-4 h-4 text-muted-foreground" />
              <span>{property.bathrooms} ba</span>
            </div>
            {property.sqft && (
              <div className="flex items-center gap-1">
                <Ruler className="w-4 h-4 text-muted-foreground" />
                <span>{property.sqft.toLocaleString()} sf</span>
              </div>
            )}
          </div>

          {/* Agent & Brokerage Info - RECA Compliant */}
          {(property.agent_name || property.brokerage_name) && (
            <div className="mt-3 pt-3 border-t">
              {property.agent_name && (
                <p className="text-sm font-medium truncate">{property.agent_name}</p>
              )}
              {property.brokerage_name && (
                <p className="text-xs text-muted-foreground truncate">{property.brokerage_name}</p>
              )}
            </div>
          )}
        </div>
      </article>
    </Link>
  );
});
